import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { DollarSign, TrendingUp, AlertTriangle, MapPin, Briefcase, Package } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatCard({ label, value, icon: Icon, tint, testid }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-xs tracking-[0.2em] uppercase text-gray-500">{label}</p>
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${tint}`}>
          <Icon size={16} />
        </div>
      </div>
      <p className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 tabular-nums">{value}</p>
    </div>
  );
}

export default function ReportsPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/reports/spend");
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <Layout><div className="p-10 text-center text-sm text-gray-500">Loading reports…</div></Layout>;
  if (!report) return <Layout><div className="p-10 text-center text-sm text-gray-500">No data yet.</div></Layout>;

  const t = report.totals;
  const monthName = new Date(report.month_start).toLocaleString(undefined, { month: "long", year: "numeric" });

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-10">
        <div className="mb-8">
          <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Admin</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Cost reports</h1>
          <p className="text-sm text-gray-600 mt-2">
            Track maintenance spend across all locations. Showing month-to-date and lifetime totals.
          </p>
        </div>

        {/* Stat row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard testid="rep-mtd" label={`Spent · ${monthName}`} value={fmt(t.mtd)} icon={DollarSign} tint="bg-[#0055FF]/10 text-[#0055FF]" />
          <StatCard testid="rep-life" label="Lifetime spend" value={fmt(t.lifetime)} icon={TrendingUp} tint="bg-emerald-50 text-emerald-600" />
          <StatCard testid="rep-mtd-items" label="Issues this month" value={t.items_mtd} icon={MapPin} tint="bg-gray-100 text-gray-700" />
          <StatCard testid="rep-life-items" label="Issues lifetime" value={t.items_lifetime} icon={Briefcase} tint="bg-gray-100 text-gray-700" />
        </div>

        {/* Low stock notice */}
        {report.low_stock_parts?.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 mb-8 flex items-start gap-3" data-testid="low-stock-notice">
            <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-display font-semibold text-amber-900">
                {report.low_stock_parts.length} part{report.low_stock_parts.length === 1 ? "" : "s"} running low
              </p>
              <p className="text-xs text-amber-800 mt-1">
                {report.low_stock_parts
                  .slice(0, 6)
                  .map((p) => `${p.name} (${p.quantity})`)
                  .join(" · ")}
                {report.low_stock_parts.length > 6 && " · …"}
              </p>
            </div>
          </div>
        )}

        {/* By location */}
        <section className="mb-10">
          <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Spend by location</h2>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="loc-spend">
            {report.by_location.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">No spend recorded yet.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {report.by_location.map((row) => {
                  const pct = row.budget_used_pct;
                  const budget = row.monthly_budget;
                  const over = pct != null && pct > 100;
                  return (
                    <div
                      key={row.location_id}
                      data-testid={`loc-row-${row.location_id}`}
                      className="px-5 py-4 grid grid-cols-12 items-center gap-3"
                    >
                      <div className="col-span-12 sm:col-span-3 flex items-center gap-2">
                        <div className="w-8 h-8 rounded-md bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center shrink-0">
                          <MapPin size={14} />
                        </div>
                        <span className="font-medium truncate">{row.location_name}</span>
                      </div>
                      <div className="col-span-6 sm:col-span-2 text-sm">
                        <div className="text-xs uppercase tracking-wider text-gray-500">{monthName}</div>
                        <div className="font-display font-bold tabular-nums">{fmt(row.mtd)}</div>
                      </div>
                      <div className="col-span-6 sm:col-span-2 text-sm">
                        <div className="text-xs uppercase tracking-wider text-gray-500">Lifetime</div>
                        <div className="font-display font-bold tabular-nums text-gray-700">{fmt(row.lifetime)}</div>
                      </div>
                      <div className="col-span-6 sm:col-span-1 text-sm">
                        <div className="text-xs uppercase tracking-wider text-gray-500">Issues</div>
                        <div className="font-semibold tabular-nums">{row.count}</div>
                      </div>
                      <div className="col-span-6 sm:col-span-4">
                        {budget ? (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-500">Budget {fmt(budget)}</span>
                              <span className={over ? "text-red-600 font-bold" : "text-gray-700"}>
                                {pct?.toFixed(0)}%
                              </span>
                            </div>
                            <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                              <div
                                className={`h-full transition-all ${
                                  over ? "bg-red-500" : pct > 80 ? "bg-amber-500" : "bg-emerald-500"
                                }`}
                                style={{ width: `${Math.min(100, pct || 0)}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">No budget set</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* By vendor */}
        <section>
          <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Spend by vendor</h2>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="vendor-spend">
            {report.by_vendor.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">No vendor spend recorded yet.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {report.by_vendor.map((row) => (
                  <div key={row.vendor_name} className="px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center">
                        <Briefcase size={14} />
                      </div>
                      <div>
                        <div className="font-medium">{row.vendor_name}</div>
                        <div className="text-xs text-gray-500">{row.count} issue{row.count === 1 ? "" : "s"}</div>
                      </div>
                    </div>
                    <div className="font-display font-bold tabular-nums">{fmt(row.lifetime)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}
